import * as React from "react";
import { connect } from "react-redux";

import { IconImage } from "common/components/SerializedApp";
import { Pricing } from "common/Database/pricing";
import ActionModal from "../action-modal/action-modal";
import { formatStripePrice } from "webapp/lib/stripe-ui";
import { Wrapper, Author, Title, Disclaimer } from "./buy-template-modal-style";
import { Icon } from "common/render/icon";
import { UserDataAndState, Organization, isUserData, UserData } from "common/Database";
import { RootState } from "store/types";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import { panic } from "common/Support";
import { GlideDropdown } from "designer/ui/smart/glide-dropdown/glide-dropdown";
import AppIcon from "common/app-icon";
import { isPrivateProTrialTemplate } from "designer/builder/lib/builder-first-run-templates";
import { useOnceCallback } from "hooks/use-once-callback";
import { useOverridesBusy } from "designer/designer";
import { ItemDescription } from "designer/lib/dropdown-types";

export type BuyTemplateModalResponse = {
    ownerID: string;
    nextAction: "show-checkout" | "copy-template" | "do-nothing";
};

interface Props {
    userData: UserDataAndState;
    organizations: readonly Organization[];
    organizationsLoaded: boolean;
    templateID: string;
    templateInfo: TemplateInfo;
    followUpAction: (resp: BuyTemplateModalResponse) => void;
}

export type TemplateInfo = Partial<{
    iconImage: IconImage;
    primaryAccentColor: string;
    title: string;
    author: string;
    pricing: Pricing;
}>;

type OrgChoice = { ownerID: string; icon: React.ReactNode; ownerName: string };

function orgChoicesFromUserData(userData: UserData, organizations: readonly Organization[], isProTrial: boolean) {
    const myApps = {
        ownerID: userData.id,
        icon: (
            <div className={"rc-menu-icon"}>
                {userData.photoURL === undefined ? (
                    <AppIcon icon="16-02-folder-empty-1" />
                ) : (
                    <Icon size={24} image={{ url: userData.photoURL }} color={"#3f3f3f"} clipCorners={true} />
                )}
            </div>
        ),
        ownerName: userData.displayName ?? "You",
    };
    const orgListing = organizations.map(o => ({
        ownerID: o.id,
        icon: (
            <div className={"rc-menu-icon"}>
                <AppIcon icon="16-02-folder-empty-1" />
            </div>
        ),
        ownerName: o.displayName,
    }));

    // If it is a pro trial, only show orgs since we don't support pro trials for my apps
    // Additionally, we might not be showing "My Apps" in the modal anymore.
    return isProTrial || userData.allowMyApps === false ? [...orgListing] : [myApps, ...orgListing];
}

function descriptionForOrgChoice(c: OrgChoice): ItemDescription {
    return { name: c.ownerName, icon: c.icon };
}

const BuyTemplateModalImpl: React.FC<Props> = p => {
    const {
        userData,
        organizations,
        organizationsLoaded,
        followUpAction,
        templateInfo: { iconImage, title, author, primaryAccentColor, pricing },
        templateID,
    } = p;
    const [selectedOwnerID, setSelectedOwnerID] = React.useState<string>();

    useOverridesBusy();

    const knownPrice = pricing ?? { currency: "USD", value: 0 };

    const getSelectedOwnerID = React.useCallback((): string => {
        if (selectedOwnerID !== undefined) return selectedOwnerID;
        if (isUserData(userData)) return userData.id;
        return panic("Could not get selected owner");
    }, [selectedOwnerID, userData]);

    const onAccept = useOnceCallback(() => {
        followUpAction({
            ownerID: getSelectedOwnerID(),
            nextAction: knownPrice.value > 0 ? "show-checkout" : "copy-template",
        });
    }, [followUpAction, getSelectedOwnerID, knownPrice.value]);

    const onReject = useOnceCallback(() => {
        followUpAction({ ownerID: "", nextAction: "do-nothing" });
    }, [followUpAction]);

    const onItemSelected = (i: OrgChoice) => {
        setSelectedOwnerID(i.ownerID);
    };

    const isPaid = knownPrice.value > 0;
    const authorName = author ?? "Unknown Author";

    const callToAction = isPaid ? `Buy for ${formatStripePrice(knownPrice.value)}` : "Copy";

    if (!isUserData(userData) || !organizationsLoaded) {
        return (
            <ActionModal
                header={title}
                description={
                    <Wrapper>
                        <LoadingIndicator />
                    </Wrapper>
                }
                cancelAction={onReject}
            />
        );
    }

    const isProTrial = isPrivateProTrialTemplate(templateID);
    const dropdownChoices = orgChoicesFromUserData(userData, organizations, isProTrial);
    const selectedChoice = dropdownChoices.find(c => c.ownerID === selectedOwnerID) ?? dropdownChoices[0];

    return (
        <ActionModal
            header={title}
            description={
                <Wrapper>
                    {iconImage !== undefined && primaryAccentColor !== undefined && (
                        <Icon size={95} clipCorners={true} color={primaryAccentColor} image={iconImage} />
                    )}
                    <Title>{title ?? "Unnamed Template"}</Title>
                    <Author>by {authorName}</Author>
                    <Disclaimer>
                        Glide offers no warranty or support for templates.
                        {isPaid && <div>Glide subscription sold separately.</div>}
                    </Disclaimer>
                    <div className="buy-template-dropdown-container">
                        <h4 className="buy-template-dropdown-header">Owner:</h4>
                        <GlideDropdown<OrgChoice>
                            selected={selectedChoice}
                            items={dropdownChoices}
                            tallerVariation={true}
                            onItemSelect={onItemSelected}
                            descriptionForItem={descriptionForOrgChoice}
                        />
                        <span className="buy-template-copy-disclaimer">
                            Templates cannot be transferred between owners.
                        </span>
                    </div>
                </Wrapper>
            }
            acceptText={callToAction}
            acceptAction={onAccept}
            cancelAction={onReject}
        />
    );
    // }
};

const mapState = (state: RootState) => ({
    userData: state.userData,
    organizations: state.organizations,
    organizationsLoaded: state.organizationsLoaded,
});

const BuyTemplateModal = connect(mapState)(BuyTemplateModalImpl);
export default BuyTemplateModal;
